import React, { useEffect, useRef, useState } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBiometric } from '../hooks/useBiometric';
import {
  Alert, Avatar, Box, Button, ButtonBase, CircularProgress, Snackbar, Typography,
} from '@mui/material';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { AuthDialog } from './AuthDialog';
import { Gateway } from '../types';
import { MAX_WIDTH } from '../App';
import { statesUrl, toggleUrl, parseStates } from '../boardApi';

interface Props {
  gateway: Gateway | null;
  onChange: (gateway: Gateway) => void;
}

const statusColor = (s: boolean | null) => {
  if (s === null) return '#FFA726';
  return s ? '#EF5350' : '#4CAF50';
};

export const AlarmTab: React.FC<Props> = ({ gateway, onChange }) => {
  const { t } = useLanguage();
  const { getAuthHeader, activeHouseId } = useAuth();
  const biometric = useBiometric(activeHouseId ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const mountedRef = useRef(false);

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const status = gateway?.nodes[0]?.state ?? null;

  const statusLabel =
    status === null ? t.alarmStatusUnknown :
    status ? t.alarmStatusArmed : t.alarmStatusDisarmed;

  const fetchStatus = async (gw: NonNullable<typeof gateway>) => {
    setIsRefreshing(true);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(statesUrl(gw.url), { headers: getAuthHeader(), signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const states = parseStates(html);
      onChange({
        ...gw,
        lastFetchedAt: Date.now(),
        nodes: gw.nodes.map((n, i) => ({
          ...n,
          state: i < states.length ? states[i] : null,
          error: undefined,
        })),
      });
    } catch {
      onChange({ ...gw, nodes: gw.nodes.map(n => ({ ...n, state: null })) });
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!gateway) return;

    if (!mountedRef.current) {
      mountedRef.current = true;
      if (gateway.lastFetchedAt && Date.now() - gateway.lastFetchedAt < 30_000) return;
    }

    let cancelled = false;
    fetchStatus(gateway).then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
  }, [gateway?.id, gateway?.url, internalRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useAutoRefresh(() => setInternalRefreshKey(k => k + 1), 300_000);

  const doToggle = async () => {
    if (!gateway) return;
    if (!gateway.url) { setToggleError(t.errorNoUrl); return; }
    setIsLoading(true);
    setToggleError(null);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(toggleUrl(gateway.url, 0), {
        headers: getAuthHeader(),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (response.ok) {
        // /relays.cgi's toggle endpoint answers with the same real states array
        // the poll returns -- read the panel's own state back instead of assuming
        // the inverse locally, which can drift from what actually happened.
        const html = await response.text();
        const states = parseStates(html);
        const realState = states.length > 0 ? states[0] : status !== true;
        const newNodes = [...gateway.nodes];
        newNodes[0] = { ...newNodes[0], state: realState };
        onChange({ ...gateway, lastFetchedAt: Date.now(), nodes: newNodes });
      } else {
        setToggleError(t.errorHttp(response.status));
      }
    } catch {
      setToggleError(t.errorConnection);
    }
    setIsLoading(false);
  };

  const handleToggle = async () => {
    if (!gateway?.url || isLoading) return;
    if (biometric.isEnrolled) {
      const ok = await biometric.authenticate();
      if (ok) { await doToggle(); return; }
    }
    setAuthDialogOpen(true);
  };

  return (
    <Box sx={{
      maxWidth: MAX_WIDTH,
      mx: 'auto',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100dvh - 56px - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
      px: 3,
      pt: 2,
      pb: 2,
    }}>

      {/* Bell icon + title + description */}
      <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" mt={4}>
        <Avatar sx={{
          width: 140,
          height: 140,
          bgcolor: '#C62828',
          boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
          mb: 3,
        }}>
          <span style={{ fontSize: 72 }}>🔔</span>
        </Avatar>

        <Typography variant="h4" letterSpacing={3} mb={2}>
          {t.alarmTitle}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
          {t.alarmDescription}
        </Typography>
      </Box>

      <Box flex={1} />

      {/* Status — tap to refresh */}
      <ButtonBase
        aria-label={t.a11yRefreshStatus}
        disabled={!gateway || isRefreshing}
        onClick={() => gateway && !isRefreshing && fetchStatus(gateway)}
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 1, mb: 2, py: 0.5, borderRadius: 1,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        {isRefreshing
          ? <CircularProgress size={12} sx={{ color: statusColor(status) }} />
          : <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor(status) }} />
        }
        <Typography variant="body1" role="status" sx={{ color: statusColor(status) }}>
          {t.alarmStatusLabel}: {statusLabel}
        </Typography>
      </ButtonBase>

      {/* Action button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleToggle}
        disabled={isLoading || !gateway?.url}
        sx={{
          bgcolor: '#C62828',
          '&:hover': { bgcolor: '#B71C1C' },
          py: 1.5,
          fontSize: 16,
          fontWeight: 'bold',
          borderRadius: 2,
          mb: 1,
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : status === null ? '···' : status ? t.alarmDisarm : t.alarmArm}
      </Button>

      <AuthDialog
        open={authDialogOpen}
        onSuccess={() => { setAuthDialogOpen(false); doToggle(); }}
        onClose={() => setAuthDialogOpen(false)}
      />

      <Snackbar
        open={toggleError !== null}
        autoHideDuration={5000}
        onClose={() => setToggleError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToggleError(null)} sx={{ width: '100%' }}>
          {toggleError}
        </Alert>
      </Snackbar>

    </Box>
  );
};
